'use client';

import { createContext, useContext, useMemo, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/components/auth-provider';
import { UAMPBrowserClient } from '@/lib/uamp/client';

interface UAMPContextType {
  client: UAMPBrowserClient | null;
  isConnected: boolean;
}

const UAMPContext = createContext<UAMPContextType>({
  client: null,
  isConnected: false,
});

export function useUAMPContext() {
  return useContext(UAMPContext);
}

const POLL_MS = 5_000;

// Persist the UAMP client and token on `window` so they survive Fast Refresh
// module reloads, which otherwise destroy the WebSocket on every rebuild.
function getGlobalClient(): UAMPBrowserClient {
  const w = window as unknown as { __uampClient?: UAMPBrowserClient };
  if (!w.__uampClient) w.__uampClient = new UAMPBrowserClient();
  return w.__uampClient;
}
function getGlobalToken(): string | null {
  return (window as unknown as { __uampToken?: string | null }).__uampToken ?? null;
}
function setGlobalToken(t: string | null) {
  (window as unknown as { __uampToken?: string | null }).__uampToken = t;
}

export function UAMPProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);

  const client = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return getGlobalClient();
  }, []);

  useEffect(() => {
    if (!client) return;

    if (!user) {
      client.disconnect();
      setGlobalToken(null);
      setIsConnected(false);
      return;
    }

    // If the client already has a live connection (e.g. preserved across Fast
    // Refresh), just sync React state and skip reconnect.
    if (client.isConnected) {
      setIsConnected(true);
    }

    let cancelled = false;
    let fetchingToken = false;

    const tryConnect = async () => {
      if (client.isConnected || client.isConnecting || fetchingToken) return;

      try {
        if (client.takeAuthRejected()) setGlobalToken(null); // 4001: this token is finished, the session route has the live one
        let token = getGlobalToken();
        if (!token) {
          fetchingToken = true;
          const res = await fetch('/api/auth/session');
          fetchingToken = false;
          if (!res.ok || cancelled) return;
          const data = await res.json();
          if (cancelled) return;
          if (!data.token) return;
          token = data.token;
          setGlobalToken(token);
        }
        if (client.isConnected || client.isConnecting) return;
        if (token) client.connect(token);
      } catch {
        fetchingToken = false;
      }
    };

    tryConnect();

    const timer = setInterval(() => {
      if (cancelled) return;
      const connected = client.isConnected;
      setIsConnected(connected);
      if (!connected) tryConnect();
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [client, user]);

  const value = useMemo(
    () => ({ client, isConnected }),
    [client, isConnected]
  );

  return (
    <UAMPContext.Provider value={value}>
      {children}
    </UAMPContext.Provider>
  );
}
